// ============================================================================
// marketGeo.ts — КАНОНИЧЕСКАЯ ГЕО-СХЕМА МАРКЕТПЛЕЙСА В ИНТЕРФЕЙСЕ (клиент).
//
// Один модуль на все экраны (мастер заявки, кабинет мастера, лента):
//   • загрузка иерархии справочников ЯДРА: страны ← регионы ← {города, районы};
//   • значение гео (ссылки + координаты + радиус) и преобразование в колонки
//     таблицы mkt_requests/mkt_masters;
//   • проверка ввода ДО отправки (диапазоны координат, пара lat/lon, радиус);
//   • отбор ленты по тем же правилам, что и сервер (markettake/geo_match.go):
//     пустая ссылка/координаты — НЕИЗВЕСТНОЕ измерение (запись проходит),
//     отсекает только явное несовпадение ссылок или выход за радиус;
//   • более строгий радиус из двух (фильтр/запись) — как на сервере.
//
// Новые справочники не создаются: используются countries/regions/cities/areas
// поставки ядра (владелец записи — owner_id, как в механизме владельцев 1С).
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { readThrough } from '@/lib/offlineCache';
import { createLogger } from '@/lib/logger';

const log = createLogger('market:geo');

/** Уровни гео-иерархии (страна → регион → город/район). */
export type MarketGeoLevel = 'country' | 'region' | 'city' | 'area';

const GEO_TABLES: Record<MarketGeoLevel, string> = {
  country: 'countries',
  region: 'regions',
  city: 'cities',
  area: 'areas',
};

/** Родительский уровень: регион принадлежит стране, город/район — региону. */
const GEO_PARENT_LEVEL: Record<MarketGeoLevel, MarketGeoLevel | null> = {
  country: null,
  region: 'country',
  city: 'region',
  area: 'region',
};

const GEO_OPTION_LIMIT = 500;
const GEO_OPTION_MAX_PAGES = 20;

export interface MarketGeoOption {
  id: string;
  name: string;
  parentId: string;
}

/** Гео-значение формы: ссылки, координаты и радиус (строки — как в полях ввода). */
export interface MarketGeoValue {
  countryId: string;
  regionId: string;
  cityId: string;
  areaId: string;
  lat: string;
  lon: string;
  radiusKm: string;
}

export const EMPTY_MARKET_GEO: MarketGeoValue = {
  countryId: '',
  regionId: '',
  cityId: '',
  areaId: '',
  lat: '',
  lon: '',
  radiusKm: '',
};

/** Гео строки таблицы: ссылки + числовые значения (null — не задано/скрыто). */
export interface MarketGeoRow {
  countryId: string;
  regionId: string;
  cityId: string;
  areaId: string;
  lat: number | null;
  lon: number | null;
  radiusKm: number | null;
}

/** Отбор ленты по гео (в памяти; правила сервера). */
export interface MarketGeoFilter {
  countryId: string;
  regionId: string;
  cityId: string;
  areaId: string;
  lat: number | null;
  lon: number | null;
  radiusKm: number | null;
}

/** Допуск границы радиуса (как в markettake/geo_match.go: 1e-9 км). */
const GEO_DISTANCE_EPSILON_KM = 1e-9;

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  return s === '<nil>' ? '' : s;
}

function numberOf(value: unknown): number | null {
  const raw = text(value);
  if (!raw) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function isTrue(value: unknown): boolean {
  const s = text(value).toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

/** Разбор гео-колонок строки таблицы (ListTableData). */
export function marketGeoFromCells(cells: Map<string, string>): MarketGeoRow {
  return {
    countryId: text(cells.get('country_id')),
    regionId: text(cells.get('region_id')),
    cityId: text(cells.get('city_id')),
    areaId: text(cells.get('area_id')),
    lat: numberOf(cells.get('geo_lat')),
    lon: numberOf(cells.get('geo_lon')),
    radiusKm: numberOf(cells.get('radius_km')),
  };
}

/** Есть ли в гео-значении хоть одно заполненное поле. */
export function marketGeoHasValue(geo: MarketGeoValue | null | undefined): boolean {
  if (!geo) return false;
  return Boolean(
    geo.countryId.trim()
    || geo.regionId.trim()
    || geo.cityId.trim()
    || geo.areaId.trim()
    || geo.lat.trim()
    || geo.lon.trim()
    || geo.radiusKm.trim(),
  );
}

/** Есть ли в гео-строке хоть одна ссылка иерархии. */
export function marketGeoRowHasRefs(geo: MarketGeoRow | null | undefined): boolean {
  if (!geo) return false;
  return Boolean(geo.countryId || geo.regionId || geo.cityId || geo.areaId);
}

function parseCoordinate(raw: string): number | null {
  const value = raw.trim().replace(',', '.');
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Гео-значение формы → отбор ленты. */
export function marketGeoFilter(value: MarketGeoValue | null | undefined): MarketGeoFilter {
  return {
    countryId: String(value?.countryId || '').trim(),
    regionId: String(value?.regionId || '').trim(),
    cityId: String(value?.cityId || '').trim(),
    areaId: String(value?.areaId || '').trim(),
    lat: parseCoordinate(String(value?.lat || '')),
    lon: parseCoordinate(String(value?.lon || '')),
    radiusKm: parseCoordinate(String(value?.radiusKm || '')),
  };
}

/** Отбор задан (есть ссылки или координаты/радиус). */
export function marketGeoFilterActive(filter: MarketGeoFilter): boolean {
  // Радиус без центра не является отбором: сервер требует пару координат
  // (feed_geo.go), а «радиус сам по себе» ничего не ограничивает.
  return Boolean(
    filter.countryId
    || filter.regionId
    || filter.cityId
    || filter.areaId
    || validPoint(filter.lat, filter.lon),
  );
}

function validPoint(lat: number | null, lon: number | null): boolean {
  if (lat === null || lon === null) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

function compatibleId(left: string, right: string): boolean {
  const a = text(left);
  const b = text(right);
  if (!a || !b) return true; // неизвестное измерение не отсекает
  return a.toLowerCase() === b.toLowerCase();
}

/** Более строгий из заданных радиусов (nil — ограничения нет). */
function stricterRadius(a: number | null, b: number | null): number | null {
  const left = a !== null && a > 0 ? a : null;
  const right = b !== null && b > 0 ? b : null;
  if (left !== null && right !== null) return Math.min(left, right);
  return left ?? right;
}

/** Расстояние по формуле Haversine (км). */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371.0088;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Проходит ли гео записи отбор. Повторяет серверные правила:
 * явное различие ссылок или выход за радиус — отсекает; неизвестное
 * измерение (пустая ссылка/координаты) — не отсекает.
 */
export function marketGeoMatches(filter: MarketGeoFilter, row: MarketGeoRow): boolean {
  if (!compatibleId(filter.countryId, row.countryId)
    || !compatibleId(filter.regionId, row.regionId)
    || !compatibleId(filter.cityId, row.cityId)
    || !compatibleId(filter.areaId, row.areaId)) {
    return false;
  }
  if (!validPoint(filter.lat, filter.lon)) return true;
  if (!validPoint(row.lat, row.lon)) return true;
  const radius = stricterRadius(filter.radiusKm, row.radiusKm);
  if (radius === null) return true;
  const distance = haversineKm(filter.lat as number, filter.lon as number, row.lat as number, row.lon as number);
  return distance <= radius + GEO_DISTANCE_EPSILON_KM;
}

/** Расстояние от точки до записи (км); null — координаты неизвестны. */
export function marketGeoDistance(
  origin: { lat: number | null; lon: number | null } | null | undefined,
  row: MarketGeoRow,
): number | null {
  if (!origin || !validPoint(origin.lat, origin.lon)) return null;
  if (!validPoint(row.lat, row.lon)) return null;
  return haversineKm(origin.lat as number, origin.lon as number, row.lat as number, row.lon as number);
}

/**
 * Проверка гео-значения перед сохранением. Возвращает текст ошибки или ''.
 * Пустое гео допустимо; заданные поля проверяются согласованно с сервером.
 */
export function validateMarketGeo(geo: MarketGeoValue): string {
  const latRaw = geo.lat.trim();
  const lonRaw = geo.lon.trim();
  const lat = parseCoordinate(latRaw);
  const lon = parseCoordinate(lonRaw);
  if (latRaw && (lat === null || lat < -90 || lat > 90)) {
    return 'Широта должна быть числом от -90 до 90';
  }
  if (lonRaw && (lon === null || lon < -180 || lon > 180)) {
    return 'Долгота должна быть числом от -180 до 180';
  }
  if ((latRaw === '') !== (lonRaw === '')) {
    return 'Широта и долгота задаются вместе';
  }
  const radiusRaw = geo.radiusKm.trim();
  if (radiusRaw) {
    const radius = parseCoordinate(radiusRaw);
    if (radius === null || radius <= 0) return 'Радиус должен быть числом больше нуля';
  }
  if (geo.cityId && !geo.regionId) {
    return 'Сначала выберите регион, затем город';
  }
  if (geo.areaId && !geo.regionId) {
    return 'Сначала выберите регион, затем район';
  }
  return '';
}

/** Гео-значение формы → значения колонок таблицы (пустые строки очищают). */
export function marketGeoToValues(geo: MarketGeoValue): Record<string, string> {
  return {
    country_id: geo.countryId.trim(),
    region_id: geo.regionId.trim(),
    city_id: geo.cityId.trim(),
    area_id: geo.areaId.trim(),
    geo_lat: geo.lat.trim().replace(',', '.'),
    geo_lon: geo.lon.trim().replace(',', '.'),
    radius_km: geo.radiusKm.trim().replace(',', '.'),
  };
}

/** Гео-строка таблицы → значение формы (для редактирования). */
export function marketGeoToValue(row: MarketGeoRow | null | undefined): MarketGeoValue {
  if (!row) return { ...EMPTY_MARKET_GEO };
  const numberText = (value: number | null) => (value === null ? '' : String(value));
  return {
    countryId: row.countryId,
    regionId: row.regionId,
    cityId: row.cityId,
    areaId: row.areaId,
    lat: numberText(row.lat),
    lon: numberText(row.lon),
    radiusKm: numberText(row.radiusKm),
  };
}

function optionFromRow(row: TableDataRow): MarketGeoOption | null {
  const cells = new Map<string, string>();
  for (const v of row.values || []) {
    if (v.value === '<nil>') continue;
    cells.set(String(v.column), String(v.value ?? ''));
  }
  const id = String(row.id || '');
  if (!id) return null;
  if (isTrue(cells.get('is_marked'))) return null;
  const name = text(cells.get('name')) || text(cells.get('synonym')) || text(cells.get('code')) || id;
  return { id, name, parentId: text(cells.get('owner_id')) };
}

async function fetchGeoOptions(level: MarketGeoLevel, parentId: string): Promise<MarketGeoOption[]> {
  const table = GEO_TABLES[level];
  const parentLevel = GEO_PARENT_LEVEL[level];
  const filters = parentLevel && parentId ? [{ column: 'owner_id', value: parentId }] : [];
  const out: MarketGeoOption[] = [];
  const sdk = getSDK();
  for (let page = 0; page < GEO_OPTION_MAX_PAGES; page++) {
    const res = await sdk.metadata.listTableData({
      schema: 'public',
      table,
      pageSize: GEO_OPTION_LIMIT,
      page,
      includeMarked: false,
      orderBy: 'name',
      filters,
    });
    const rows = res.rows;
    for (const row of rows) {
      const option = optionFromRow(row);
      if (option) out.push(option);
    }
    if (rows.length < GEO_OPTION_LIMIT) break;
  }
  out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  return out;
}

/**
 * Варианты справочника уровня. Для не-страны без выбранного родителя
 * возвращается пустой список (каскад: сначала выберите страну/регион).
 * Ошибка чтения пробрасывается — интерфейс показывает её текстом.
 */
export async function loadMarketGeoOptions(
  level: MarketGeoLevel,
  parentId = '',
): Promise<MarketGeoOption[]> {
  const parentLevel = GEO_PARENT_LEVEL[level];
  if (parentLevel && !parentId.trim()) return [];
  const key = `market:geo:${level}:${parentId.trim() || 'root'}`;
  const cached = await readThrough<MarketGeoOption[]>(key, () => fetchGeoOptions(level, parentId.trim()));
  return cached.data;
}

/** Подписи выбранных гео-уровней (для сводки активных фильтров). */
export type MarketGeoLabelMap = Partial<Record<MarketGeoLevel, string>>;

async function fetchGeoLabel(level: MarketGeoLevel, id: string): Promise<string> {
  const res = await getSDK().metadata.listTableData({
    schema: 'public',
    table: GEO_TABLES[level],
    pageSize: 1,
    page: 0,
    includeMarked: false,
    filters: [{ column: 'id', value: id }],
  });
  const row = res.rows[0];
  if (!row) return '';
  const option = optionFromRow(row);
  return option ? option.name : '';
}

/**
 * Названия выбранных гео-ссылок для сводки фильтров. Читается по одной записи
 * уровня (кэш чтения — офлайн доступен последний сохранённый вариант).
 */
export async function loadMarketGeoLabels(geo: MarketGeoValue): Promise<MarketGeoLabelMap> {
  const levels: Array<[MarketGeoLevel, string]> = [
    ['country', geo.countryId.trim()],
    ['region', geo.regionId.trim()],
    ['city', geo.cityId.trim()],
    ['area', geo.areaId.trim()],
  ];
  const labels: MarketGeoLabelMap = {};
  await Promise.all(levels.map(async ([level, id]) => {
    if (!id) return;
    const cached = await readThrough<string>(
      `market:geo:label:${level}:${id}`,
      () => fetchGeoLabel(level, id),
    );
    if (cached.data) labels[level] = cached.data;
  }));
  return labels;
}

/** Единая подпись ошибки чтения справочника (без технических кодов). */
export function marketGeoFailureText(level: MarketGeoLevel): string {
  const labels: Record<MarketGeoLevel, string> = {
    country: 'страны',
    region: 'регионы',
    city: 'города',
    area: 'районы',
  };
  return `Не удалось загрузить ${labels[level]}`;
}

/** Лог ошибки гео-справочника (без внешних зависимостей в компонентах). */
export function logMarketGeoError(e: unknown, what: string): void {
  log.warn(what, { error: e });
}
